import React, { useState, useEffect } from 'react'
import { object, func, string, number, bool } from 'prop-types'
import styled, { css } from 'styled-components'
import { stripUnit, rgba } from 'polished'

import { SETTINGS_FOR_PRODUCT_NAME } from 'utils/constants'
import { trackEvent } from 'utils/tracking'

import * as spacing from 'styles/spacing'
import { media } from 'styles/media'
import { white } from 'styles/colors'
import { square, cover } from 'styles/global'
import { H4, fontWeights } from 'styles/typography'
import Modal from './CartridgeModal'
import Icon from 'components/Icon'

import CloseSmall from 'icons/close-small.svg'

const Container = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
`

const Inner = styled.div`
  position: relative;
  text-align: center;
  padding: ${spacing.large};
  padding-bottom: ${spacing.medium};
  overflow: hidden;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  background-color: #999;

  ${media.medium`
    padding: ${spacing.xLarge};
    padding-bottom: ${spacing.large};
  `};
`

const Figure = styled.img`
  position: relative;
  object-fit: cover;
  border-radius: 50%;
  margin-bottom: ${spacing.medium};
  ${square('48px')};

  ${media.medium`
    ${square('56px')};
  `};

  ${media.large`
    margin-bottom: ${stripUnit(spacing.medium) * 1.25 + 'px'};
    ${square('64px')};
  `};

  ${media.xLarge`
    ${square('72px')};
  `};
`

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%; /* Make sure content takes up entire width */
`

const Main = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  align-items: center;
  padding-bottom: ${spacing.large};
  justify-content: space-between;

  ${media.large`
    padding-bottom: ${spacing.xLarge};
  `};
`

const Name = styled(H4)`
  color: ${white};
  margin-bottom: ${spacing.small};

  ${media.medium`
    margin-bottom: ${stripUnit(spacing.small) * 1.5 + 'px'};
  `};
`

const Footer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-top: ${stripUnit(spacing.small) * 1.5 + 'px'};
  border-top: 1px solid ${rgba(white, 0.4)};
  z-index: 10;

  ${media.medium`
    margin-top: auto;
    padding-top: ${spacing.medium};
  `};

  ${media.large`
    padding-top: ${stripUnit(spacing.medium) * 1.25 + 'px'};
    padding-bottom: ${spacing.tiny};
  `};
`

const Action = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${white};
  font-weight: ${fontWeights.bold};

  ${props =>
    props.active &&
    css`
      color: ${white};
    `};

  &[disabled] {
    opacity: 0.5;
  }
`

const Frame = styled.div`
  ${cover('absolute')};
  pointer-events: none;
`

const Overlay = styled.div`
  ${cover('absolute')};
  cursor: pointer;

  &[disabled] {
    display: none;
  }
`

export const Remove = styled.button`
  display: flex;
  color: ${white};
  margin-left: ${spacing.tiny};

  ${media.large`
    margin-left: ${spacing.small};
  `};
`

const Cartridge = ({
  product,
  handleCartridgeVariantQuantityChange,
  currentCartridgeQuantity,
  calculateTotalPrice,
  priceMultiplier,
  flow,
  buttonLabel,
  deviceHandle,
  countdownBundleFlow,
  handleCartridgeRemove,
  multipackLimit
}) => {
  const [expanded, setExpanded] = useState(false)

  const quantities = product.variants.map(variants => variants.quantity)
  const quantitiesSum = quantities.reduce((a, b) => {
    return a + b
  }, 0)

  const active = quantitiesSum > 0

  const productHandle = SETTINGS_FOR_PRODUCT_NAME[product.handle]

  useEffect(() => {
    document.body.style.overflow = expanded ? 'hidden' : 'visible'
  }, [expanded])

  return (
    <>
      <Container>
        <Inner
          css={`
            background: ${productHandle?.gradient};
          `}
        >
          <Figure src={product.images[0].originalSrc} alt={product.title} />
          <Content>
            <Main>
              <Name>{product.title}</Name>
            </Main>

            <Footer>
              <>
                <Action
                  disabled={!active && multipackLimit && currentCartridgeQuantity() >= multipackLimit}
                  active={active}
                  onClick={() => {
                    setExpanded(!expanded)
                  }}
                >
                  <span>{active ? `${quantitiesSum} Added` : '+ Add'}</span>
                </Action>
                {active && (
                  <Remove
                    onClick={() => {
                      handleCartridgeRemove(product.id)
                    }}
                  >
                    <Icon icon={<CloseSmall />} />
                  </Remove>
                )}
              </>
            </Footer>
          </Content>
          <Frame />

          <Overlay
            disabled={!active && multipackLimit && currentCartridgeQuantity() >= multipackLimit}
            onClick={() => {
              setExpanded(!expanded)
            }}
          />
        </Inner>
      </Container>
      {expanded ? (
        <Modal
          name={'Bonsoir'}
          handle={product.handle}
          description={product.description}
          variants={product.variants}
          close={() => {
            setExpanded(!expanded)

            trackEvent({
              name: 'screenLoad',
              time: Date(),
              page: window.location.pathname,
              url: window.location.href,
              trigger: 'modalClose'
            })
          }}
          onAdd={handleCartridgeVariantQuantityChange}
          calculateTotalPrice={calculateTotalPrice}
          product={product}
          flow={flow}
          priceMultiplier={priceMultiplier}
          currentCartridgeQuantity={currentCartridgeQuantity}
          multipackLimit={multipackLimit}
          deviceHandle={deviceHandle}
          buttonLabel={buttonLabel || 'Add'}
          countdownBundleFlow={countdownBundleFlow}
        />
      ) : null}
    </>
  )
}

Cartridge.propTypes = {
  product: object.isRequired,
  deviceHandle: string,
  handleCartridgeVariantQuantityChange: func.isRequired,
  calculateTotalPrice: func,
  currentCartridgeQuantity: func,
  flow: string,
  priceMultiplier: number,
  buttonLabel: string,

  countdownBundleFlow: bool,
  handleCartridgeRemove: func,
  multipackLimit: number
}

export default Cartridge
